import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    FiSave,
    FiCheckCircle,
    FiClock,
    FiAlertCircle,
    FiAlertTriangle,
    FiPlus,
} from 'react-icons/fi';
import { MdOutlineVaccines } from 'react-icons/md';
import { useChildProfile } from './ChildProfileLayout';
import { useVaccinations } from '../../hooks/useVaccinations';
import {
    PageHeader,
    MetricCard,
    ConfirmDialog,
    EmptyState,
    SkeletonTableRow,
    SkeletonTabs,
    useToast,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import { parseApiError } from '../../api/errors';
import { formatDateDisplay, calculateAge } from '../../utils/dates';
import { buildPdfReport } from '../../utils/pdfReport';
import DateInput from '../Common/DateInput';
import {
    VACCINE_STATUS,
    computeDynamicStatus,
    vaccineSeverity,
} from '../../utils/vaccineStatus';
import './ChildProfile.css';
import './Vaccination.css';

const PLACE_OPTIONS = [
    'MOH Clinic',
    'Hospital',
    'Private Clinic',
    'Outreach Clinic',
];

const Vaccination = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();

    const { data: profile, setPdfExporter } = useChildProfile();
    const { records: apiRecords, loading, bulkUpdate, refetch } = useVaccinations(id);

    const [vaccinations, setVaccinations] = useState([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(null); // { title, body, onConfirm }

    // Seed local editable state from API when records arrive
    useEffect(() => {
        if (apiRecords && apiRecords.length > 0 && vaccinations.length === 0) {
            setVaccinations(apiRecords);
        }
    }, [apiRecords, vaccinations.length]);

    // Refetch when a vaccine is recorded via Rapid Entry. Only update
    // local editable state if the user has no unsaved changes here,
    // so we don't clobber a row the user is currently editing. The
    // initial seed is suppressed by the `vaccinations.length === 0`
    // guard above; for the rapid-entry refresh we want to overwrite
    // explicitly when safe.
    useEffect(() => {
        const handler = (e) => {
            if (Number(e.detail?.childId) !== Number(id)) return;
            refetch();
        };
        window.addEventListener('phm:vaccination-updated', handler);
        return () => window.removeEventListener('phm:vaccination-updated', handler);
    }, [id, refetch]);

    // After a refetch (e.g. triggered by Rapid Entry), mirror the new
    // server data into local state — but only if the user has no
    // unsaved edits, so we never clobber an in-progress row.
    const apiRecordsRef = useRef(apiRecords);
    useEffect(() => {
        if (apiRecordsRef.current === apiRecords) return;
        apiRecordsRef.current = apiRecords;
        if (apiRecords && apiRecords.length > 0 && !hasUnsavedChanges && vaccinations.length > 0) {
            setVaccinations(apiRecords);
        }
    }, [apiRecords, hasUnsavedChanges, vaccinations.length]);

    // Warn user before leaving the page with unsaved changes
    useEffect(() => {
        const handler = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsavedChanges]);

    const updateVaccine = useCallback((vaccineId, patch) => {
        setHasUnsavedChanges(true);
        setVaccinations((prev) =>
            prev.map((v) => (v.id === vaccineId ? { ...v, ...patch } : v)),
        );
    }, []);

    // Status flips to Completed only when Date Received + Batch No + Place
    // of Vaccination are ALL filled. With anything missing, fall back to
    // the dynamic status derived from DOB/age — but pass `null` for the
    // base status when it was Completed, so computeDynamicStatus doesn't
    // short-circuit back to Completed just because the date is still set.
    const computeStatusOnEdit = useCallback(
        (vaccine, date, batchNo, place) => {
            if (date && (batchNo || '').trim() && place) {
                return VACCINE_STATUS.COMPLETED;
            }
            const safeBase =
                vaccine.status === VACCINE_STATUS.COMPLETED ? null : vaccine.status;
            return computeDynamicStatus(profile?.dob, vaccine.age, safeBase, date);
        },
        [profile?.dob],
    );

    const handleSaveRecords = useCallback(async () => {
        const invalid = vaccinations.find(
            (v) =>
                v.status === VACCINE_STATUS.COMPLETED &&
                (!v.date || !(v.batchNo || '').trim() || !v.place),
        );
        if (invalid) {
            toast.error(
                `Please fill Date Received, Batch No, and Place of Vaccination for ${invalid.type} before saving as Completed.`,
            );
            return;
        }
        setSaving(true);
        try {
            await bulkUpdate(vaccinations);
            setHasUnsavedChanges(false);
            toast.success('Vaccination records saved.');
        } catch (err) {
            toast.error(parseApiError(err, 'Failed to save vaccination records.'));
        } finally {
            setSaving(false);
        }
    }, [vaccinations, bulkUpdate, toast]);

    const generatePDF = useCallback(() => {
        if (!profile) return;
        const stamp = new Date().toISOString().split('T')[0];
        const rows = vaccinations.map((vaccine) => {
            const type = vaccine.type === 'BCG'
                ? `${vaccine.type}\n(Scar: ${vaccine.bcgMark || 'Pending'})`
                : vaccine.type;
            return [
                vaccine.age?.replace('Completed', '').trim(),
                type,
                formatDateDisplay(vaccine.scheduledDate),
                formatDateDisplay(vaccine.date),
                vaccine.batchNo || '-',
                vaccine.place || '-',
                vaccine.computedStatus || '-',
                vaccine.remark || '-',
            ];
        });

        buildPdfReport({
            title: 'Vaccination History Report',
            subtitle: `${profile.name} · Reg ${profile.childNo} · ${profile.gender} · DOB ${formatDateDisplay(profile.dob)} · Age ${calculateAge(profile.dob)}`,
            filters: [`Child: ${profile.name} (${profile.childNo})`],
            sections: [
                {
                    heading: 'Vaccination Records',
                    head: [
                        'Age',
                        'Vaccine Type',
                        'Scheduled Date',
                        'Date Received',
                        'Batch No',
                        'Place',
                        'Status',
                        'Remarks',
                    ],
                    body: rows,
                    accent: [14, 165, 233],
                },
            ],
            fileName: `${profile.childNo}_Vacc_Report_${stamp}.pdf`,
            toast,
            emptyMessage: 'No vaccination records to export.',
        });
    }, [profile, vaccinations, toast]);

    // Register this tab's PDF exporter with the layout so the header's
    // "Export PDF" button calls it.
    useEffect(() => {
        setPdfExporter(() => generatePDF());
        return () => setPdfExporter(null);
    }, [setPdfExporter, generatePDF]);

    // Derive the live status per row, so the dropdown mirrors what the
    // server would send back on the next list call.
    const computed = useMemo(
        () =>
            vaccinations.map((v) => ({
                ...v,
                computedStatus: computeDynamicStatus(
                    profile?.dob,
                    v.age,
                    v.status,
                    v.date,
                ),
            })),
        [vaccinations, profile?.dob],
    );

    const coverage = useMemo(() => {
        const total = computed.length;
        const completed = computed.filter((v) => v.computedStatus === VACCINE_STATUS.COMPLETED).length;
        const due = computed.filter((v) => v.computedStatus === VACCINE_STATUS.DUE).length;
        const overdue = computed.filter((v) => v.computedStatus === VACCINE_STATUS.OVERDUE).length;
        const upcoming = computed.filter((v) => v.computedStatus === VACCINE_STATUS.UPCOMING).length;
        const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
        return { total, completed, due, overdue, upcoming, pct };
    }, [computed]);

    if (loading || !profile) {
        return (
            <div className="vacc-wrapper">
                <div className="cp-skeleton-stack">
                    <div className="pt-skeleton-header-card" />
                    <SkeletonTabs count={3} />
                    <div className="vacc-card">
                        <div className="vacc-card-header">
                            <h2>Vaccination Schedule</h2>
                        </div>
                        <table className="vacc-table">
                            <thead>
                                <tr>
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <th key={i}>col</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: 10 }).map((_, i) => (
                                    <SkeletonTableRow key={i} cols={8} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    const COL_HEADERS = [
        'Age',
        'Type of Vaccine',
        'Scheduled Date',
        'Date Received',
        'Batch No',
        'Place of Vaccination',
        'Status',
        'Remarks',
    ];

    return (
        <div>
            <PageHeader
                title="Vaccination Schedule"
                subtitle={
                    hasUnsavedChanges
                        ? 'You have unsaved changes.'
                        : 'Schedule and vaccination history'
                }
                icon={MdOutlineVaccines}
                className="vacc-section-header"
            />

            {vaccinations.length > 0 && (
                <div className="vacc-coverage">
                    <div className="vacc-coverage__head">
                        <div>
                            <strong>Vaccine coverage</strong>
                            <span>
                                {coverage.completed} of {coverage.total} completed
                            </span>
                        </div>
                        <div className="vacc-coverage__pct">{coverage.pct}%</div>
                    </div>
                    <div className="vacc-coverage__bar" aria-hidden="true">
                        <span
                            className="vacc-coverage__bar-fill"
                            style={{ width: `${coverage.pct}%` }}
                        />
                    </div>
                    <div className="vacc-coverage__grid">
                        <MetricCard
                            label="Completed"
                            value={coverage.completed}
                            accent="success"
                            icon={FiCheckCircle}
                        />
                        <MetricCard
                            label="Upcoming"
                            value={coverage.upcoming}
                            accent="info"
                            icon={FiClock}
                        />
                        <MetricCard
                            label="Due"
                            value={coverage.due}
                            accent="warning"
                            icon={FiAlertCircle}
                        />
                        <MetricCard
                            label="Overdue"
                            value={coverage.overdue}
                            accent="danger"
                            icon={FiAlertTriangle}
                        />
                    </div>
                </div>
            )}

            <div className="vacc-content">
                <div className="vacc-card">
                    <div className="vacc-card-header">
                        <h2>Vaccination Records</h2>
                        <div className="vacc-card-header__actions">
                            <PageButton
                                type="button"
                                variant="ghost"
                                startIcon={<FiPlus size={14} />}
                                onClick={() => navigate('/vaccination')}
                            >
                                Add Vaccination
                            </PageButton>
                            <PageButton
                                type="button"
                                variant="primary"
                                startIcon={<FiSave size={14} />}
                                onClick={handleSaveRecords}
                                disabled={saving || !hasUnsavedChanges}
                            >
                                {saving ? 'Saving…' : 'Save'}
                            </PageButton>
                        </div>
                    </div>
                    <div className="vacc-table-container">
                        <table className="vacc-table">
                            <thead>
                                <tr>
                                    {COL_HEADERS.map((h) => (
                                        <th key={h}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {computed.map((vaccine, index) => {
                                    const isFirstOfAge =
                                        index === 0 || computed[index - 1].age !== vaccine.age;
                                    const rowSpanCount = computed.filter(
                                        (v) => v.age === vaccine.age,
                                    ).length;
                                    const ageCellClass = `vacc-age${rowSpanCount > 1 ? ' vacc-age--rowspan' : ''
                                        }`;
                                    const rowClass = isFirstOfAge
                                        ? 'vacc-row vacc-row--group-start'
                                        : 'vacc-row';
                                    // Render "Action" as a neutral "--" — the bucket
                                    // means "scheduled far in the future" and reads
                                    // more naturally in the table as a dash. Neutral
                                    // statuses (Action / unknown) collapse to a single
                                    // colour so we don't visually elevate them.
                                    const statusLabel =
                                        vaccine.computedStatus === 'Action'
                                            ? '--'
                                            : vaccine.computedStatus;
                                    const statusSeverity =
                                        vaccine.computedStatus === 'Action'
                                            ? 'neutral'
                                            : vaccineSeverity(vaccine.computedStatus);
                                    return (
                                        <tr key={vaccine.id} className={rowClass}>
                                            {isFirstOfAge && (
                                                <td
                                                    className={ageCellClass}
                                                    rowSpan={rowSpanCount}
                                                >
                                                    {vaccine.age.includes('Months Completed') ||
                                                        vaccine.age.includes('Years Completed') ? (
                                                        <div className="vacc-age-stamp">
                                                            <div className="vacc-age-stamp__big">
                                                                {vaccine.age.split(' ')[0]}
                                                            </div>
                                                            <div className="vacc-age-stamp__small">
                                                                <div>
                                                                    {vaccine.age.includes('Months')
                                                                        ? 'Months'
                                                                        : 'Years'}
                                                                </div>
                                                                <div>Completed</div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="vacc-age-text">{vaccine.age}</div>
                                                    )}
                                                </td>
                                            )}
                                            <td className="vacc-type">
                                                <span className="vacc-type__name">{vaccine.type}</span>
                                                {vaccine.type === 'BCG' && (
                                                    <div className="vacc-bcg">
                                                        <span>Scar Mark:</span>
                                                        <select
                                                            className="vacc-dropdown"
                                                            value={vaccine.bcgMark || 'Pending'}
                                                            onChange={(e) =>
                                                                updateVaccine(vaccine.id, {
                                                                    bcgMark: e.target.value,
                                                                })
                                                            }
                                                        >
                                                            <option value="Yes">Yes</option>
                                                            <option value="No">No</option>
                                                            <option value="Pending">Pending</option>
                                                        </select>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="vacc-date-display">
                                                    {formatDateDisplay(vaccine.scheduledDate)}
                                                </div>
                                            </td>
                                            <td>
                                                <DateInput
                                                    className="vacc-date-input"
                                                    value={vaccine.date || ''}
                                                    onChange={(e) => {
                                                        const newDate = e.target.value;
                                                        // Clearing the Date Received wipes the
                                                        // linked completion fields and rolls the
                                                        // status back to its default (Upcoming /
                                                        // Due / Overdue) for the row.
                                                        const patch = newDate
                                                            ? {
                                                                date: newDate,
                                                                status: computeStatusOnEdit(
                                                                    vaccine,
                                                                    newDate,
                                                                    vaccine.batchNo,
                                                                    vaccine.place,
                                                                ),
                                                            }
                                                            : {
                                                                date: '',
                                                                batchNo: '',
                                                                place: '',
                                                                bcgMark:
                                                                    vaccine.type === 'BCG'
                                                                        ? 'Pending'
                                                                        : vaccine.bcgMark,
                                                                status: computeStatusOnEdit(
                                                                    vaccine,
                                                                    '',
                                                                    '',
                                                                    '',
                                                                ),
                                                            };
                                                        updateVaccine(vaccine.id, patch);
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="vacc-text-input"
                                                    placeholder="Batch No"
                                                    value={vaccine.batchNo || ''}
                                                    onChange={(e) =>
                                                        updateVaccine(vaccine.id, {
                                                            batchNo: e.target.value,
                                                            status: computeStatusOnEdit(
                                                                vaccine,
                                                                vaccine.date,
                                                                e.target.value,
                                                                vaccine.place,
                                                            ),
                                                        })
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    className="vacc-dropdown"
                                                    value={vaccine.place || ''}
                                                    onChange={(e) =>
                                                        updateVaccine(vaccine.id, {
                                                            place: e.target.value,
                                                            status: computeStatusOnEdit(
                                                                vaccine,
                                                                vaccine.date,
                                                                vaccine.batchNo,
                                                                e.target.value,
                                                            ),
                                                        })
                                                    }
                                                >
                                                    <option value="" disabled>Select</option>
                                                    {PLACE_OPTIONS.map((p) => (
                                                        <option key={p} value={p}>
                                                            {p}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <div
                                                    className={`vacc-dropdown vacc-dropdown--readonly vacc-dropdown--severity-${statusSeverity}`}
                                                >
                                                    {statusLabel}
                                                </div>
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="vacc-text-input"
                                                    placeholder="Enter remark"
                                                    value={vaccine.remark || ''}
                                                    onChange={(e) =>
                                                        updateVaccine(vaccine.id, {
                                                            remark: e.target.value,
                                                        })
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                                {vaccinations.length === 0 && (
                                    <tr>
                                        <td colSpan={COL_HEADERS.length} className="vacc-empty-cell">
                                            <EmptyState
                                                illustration="no-data"
                                                compact
                                                title="No vaccination records"
                                                description="Add vaccines from the Vaccine Catalogue — every active child is backfilled automatically."
                                            />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {confirmDialog && (
                <ConfirmDialog
                    open
                    title={confirmDialog.title}
                    body={
                        <div>
                            <p>{confirmDialog.body}</p>
                            <div className="vacc-dialog-actions">
                                <PageButton
                                    type="button"
                                    variant="ghost"
                                    onClick={confirmDialog.onDiscard}
                                >
                                    {confirmDialog.cancelLabel}
                                </PageButton>
                            </div>
                        </div>
                    }
                    confirmLabel={confirmDialog.confirmLabel}
                    confirmSeverity={confirmDialog.confirmSeverity}
                    onConfirm={confirmDialog.onConfirm}
                    onClose={() => setConfirmDialog(null)}
                />
            )}
        </div>
    );
};

export default Vaccination;